import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { StatCard } from "@/components/stat-card";
import { Select } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { apiGet } from "@/lib/api-client";
import { asPercent } from "@/lib/format";
import { asRecord, asArray } from "@/lib/type-utils";

// Backend opportunities are _format_match objects (backend/routers/
// cross_market.py): primary_question, arbitrage_profit_pct, price_range_pct,
// prices[]{source, market_name, yes_price}. The *_pct fields are ALREADY
// percent units (backend multiplies by 100) — render with toFixed, never
// through asPercent. yes_price is a 0-1 fraction, so asPercent is correct there.
function profitPctOf(opp: Record<string, unknown>): number {
  return Number(opp.arbitrage_profit_pct ?? opp.price_range_pct ?? Number.NaN);
}

export function CrossMarketPage() {
  const [minProfitPct, setMinProfitPct] = useState(5);

  const arbitrageQuery = useQuery({
    queryKey: ["cross-market", "arbitrage", minProfitPct],
    // Backend accepts min_profit_pct (ge=0.5, le=20); min_divergence does not exist.
    queryFn: () => apiGet<unknown>(`/api/cross-market/arbitrage?min_profit_pct=${minProfitPct}`),
    refetchInterval: 60_000,
  });

  const comparisonQuery = useQuery({
    queryKey: ["cross-market", "comparisons"],
    queryFn: () => apiGet<unknown>("/api/cross-market/comparisons"),
    refetchInterval: 120_000,
  });

  const arbitrage = asRecord(arbitrageQuery.data);
  const opportunities = asArray(arbitrage.opportunities);
  const comparisons = asArray(comparisonQuery.data);

  const profitValues = opportunities
    .map((opp) => Math.abs(profitPctOf(opp)))
    .filter((value) => Number.isFinite(value));

  const totalOpportunities = opportunities.length;
  const avgProfit =
    profitValues.length > 0
      ? profitValues.reduce((sum, value) => sum + value, 0) / profitValues.length
      : 0;
  const maxProfit = profitValues.length > 0 ? Math.max(...profitValues) : 0;

  // /comparisons is served by find_polymarket_vs_kalshi_matches — derive the
  // counterpart venue from prices[].source instead of hardcoding a venue name.
  const counterpartVenue =
    comparisons
      .flatMap((comp) => asArray(comp.prices))
      .find((price) => price.source !== "polymarket")?.source ?? "Other venue";

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel
        title="Cross-Market Arbitrage"
        description="Price comparison between Polymarket, PredictIt, and other prediction markets."
      >
        {arbitrageQuery.isError || comparisonQuery.isError ? (
          <QueryErrorState
            what="cross-market data"
            onRetry={() => {
              void arbitrageQuery.refetch();
              void comparisonQuery.refetch();
            }}
          />
        ) : (
          <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
            <StatCard
              label="Active Opportunities"
              value={String(totalOpportunities)}
              tone={totalOpportunities > 0 ? "success" : "default"}
              hint="Arbitrage opportunities found"
            />
            <StatCard
              label="Avg Profit"
              value={`${avgProfit.toFixed(2)}%`}
              hint="Mean pre-fee arbitrage profit"
            />
            <StatCard
              label="Max Profit"
              value={`${maxProfit.toFixed(2)}%`}
              tone={maxProfit > 10 ? "success" : maxProfit > 5 ? "warning" : "default"}
              hint="Largest pre-fee arbitrage profit"
            />
            <StatCard
              label="Markets Compared"
              value={String(comparisons.length)}
              hint="Total market pairs"
            />
          </div>
        )}
      </Panel>

      <Panel
        title="Arbitrage Opportunities"
        description="Pre-fee arbitrage profit worth investigating"
        actions={
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Min profit:</span>
            <Select
              title="Minimum profit threshold"
              className="text-sm"
              value={minProfitPct}
              onChange={(e) => setMinProfitPct(Number(e.target.value))}
            >
              <option value={2}>2%</option>
              <option value={5}>5%</option>
              <option value={10}>10%</option>
              <option value={15}>15%</option>
            </Select>
          </div>
        }
      >
        {arbitrageQuery.isError ? (
          <QueryErrorState
            what="arbitrage opportunities"
            onRetry={() => void arbitrageQuery.refetch()}
          />
        ) : (
          <div className="max-h-[400px] space-y-3 overflow-y-auto">
            {opportunities.length === 0 ? (
              <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
                No arbitrage opportunities found with {minProfitPct}% minimum profit
              </div>
            ) : (
              opportunities.slice(0, 20).map((opp, index) => {
                const profit = profitPctOf(opp);
                const prices = asArray(opp.prices);
                const venueA = prices[0];
                const venueB = prices[1];

                return (
                  <div key={index} className="rounded-lg bg-gunmetal/50 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium">
                          {String(opp.primary_question ?? venueA?.market_name ?? "Unknown Market")}
                        </h3>
                        <div className="mt-2 grid grid-cols-2 gap-4">
                          <div className="rounded bg-black/30 p-2">
                            <div className="text-xs capitalize text-slate-400">
                              {String(venueA?.source ?? "Venue A")}
                            </div>
                            <div className="text-lg font-bold text-gold-400">
                              {asPercent(Number(venueA?.yes_price))}
                            </div>
                          </div>
                          <div className="rounded bg-black/30 p-2">
                            <div className="text-xs capitalize text-slate-400">
                              {String(venueB?.source ?? "Venue B")}
                            </div>
                            <div className="text-lg font-bold text-cyan-400">
                              {asPercent(Number(venueB?.yes_price))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${profit > 10 ? "text-green-400" : "text-yellow-400"}`}>
                          {Number.isFinite(profit) ? `${Math.abs(profit).toFixed(2)}%` : "—"}
                        </div>
                        <div className="text-xs text-slate-400">pre-fee profit</div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </Panel>

      <Panel title="Market Comparisons" description="Price comparison table across markets">
        {comparisonQuery.isError ? (
          <QueryErrorState
            what="market comparisons"
            onRetry={() => void comparisonQuery.refetch()}
          />
        ) : (
          <div className="max-h-[400px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Market</TableHead>
                  <TableHead className="capitalize">Polymarket</TableHead>
                  <TableHead className="capitalize">{String(counterpartVenue)}</TableHead>
                  <TableHead>Divergence</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="py-4 text-center text-slate-400">
                      No comparison data available
                    </TableCell>
                  </TableRow>
                ) : (
                  comparisons.slice(0, 20).map((comp, index) => {
                    const prices = asArray(comp.prices);
                    const poly = prices.find((price) => price.source === "polymarket");
                    const other = prices.find((price) => price.source !== "polymarket");
                    // price_range_pct is already percent units.
                    const divergence = Number(comp.price_range_pct);

                    return (
                      <TableRow key={index}>
                        <TableCell>
                          {String(comp.primary_question ?? poly?.market_name ?? "Unknown")}
                        </TableCell>
                        <TableCell className="font-medium text-gold-400">
                          {asPercent(Number(poly?.yes_price))}
                        </TableCell>
                        <TableCell className="font-medium text-cyan-400">
                          {asPercent(Number(other?.yes_price))}
                        </TableCell>
                        <TableCell className={`font-medium ${divergence > 10 ? "text-green-400" : divergence > 5 ? "text-yellow-400" : "text-slate-400"}`}>
                          {Number.isFinite(divergence) ? `${divergence.toFixed(1)}%` : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

    </div>
  );
}
